/**
 * Login.tsx
 * 登录页面
 *
 * 功能:
 * - 用户登录
 * - 登录失败锁定提示
 * - 跳转注册页面
 */

import { useI18n } from '../i18n';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card';
import { useToast } from '@/components/ui/useToast';

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const loginMutation = useMutation({
    mutationFn: () => authApi.login({ email, password }),
    onSuccess: (response) => {
      if (response.data.success && response.data.data) {
        if (response.data.data.requireTotp && response.data.data.tempToken) {
          setTempToken(response.data.data.tempToken);
          return;
        }
        if (response.data.data.user && response.data.data.token) {
          setAuth(response.data.data.user, response.data.data.token);
          navigate('/files');
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: t("登录失败"),
        description: error.response?.data?.error?.message || t("请检查邮箱和密码"),
        variant: 'destructive',
      });
    },
  });

  const totpMutation = useMutation({
    mutationFn: () => authApi.verifyTotp({ tempToken, code: totpCode, trustDevice }),
    onSuccess: (response) => {
      const data = response.data.data;
      if (data?.user && data.token) {
        setAuth(data.user, data.token);
        navigate('/files');
      }
    },
    onError: (error: any) => {
      toast({
        title: t("验证失败"),
        description: error.response?.data?.error?.message || t("验证码无效"),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate();
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle>{t("登录")}</CardTitle>
        <CardDescription>{t("输入您的邮箱和密码登录系统")}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              {t("邮箱")}</label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              {t("密码")}</label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center justify-end">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              {t("忘记密码？")}</Link>
          </div>
          {tempToken && (
            <div className="space-y-3 rounded-lg border p-3 bg-muted/30">
              <div className="space-y-2">
                <label htmlFor="totp" className="text-sm font-medium">
                  {t("两步验证码")}</label>
                <Input
                  id="totp"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder={t("6 位验证码或备用码")}
                  autoFocus
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="rounded border-input"
                />
                {t("在本次登录有效期内信任此设备")}</label>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          {!tempToken ? (
            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t("登录中...") : t("登录")}
            </Button>
          ) : (
            <Button
              type="button"
              className="w-full"
              disabled={!totpCode || totpMutation.isPending}
              onClick={() => totpMutation.mutate()}
            >
              {totpMutation.isPending ? t("验证中...") : t("验证并登录")}
            </Button>
          )}
          <p className="text-sm text-muted-foreground">
            {t("还没有账号？{{p0}}", { p0: ' ' })}<Link to="/register" className="text-primary hover:underline">
              {t("立即注册")}</Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
